import * as Sentry from '@sentry/nextjs'
import Head from 'next/head'
import Router from 'next/router'
import React, { Suspense, useEffect } from 'react'
import { Provider } from 'urql'

import { Loading } from '@/components/Loading'
import { GenericError } from '@/components/app-errors/GenericError'
import { isPartnerTokenError, resetUrqlClient, useUrqlClient } from '@/urql'

import '@/flutter'
import { useRouter } from 'next/router'

interface Props {
  children: React.ReactNode
}

const SCROLL_POSITIONS = new Map<string, number>()

export function GuardianLayout(props: Props) {
  const client = useUrqlClient()

  const router = useRouter()

  useEffect(() => {
    function onRouteChangeComplete() {
      const key = window.history.state.key
      if (key && SCROLL_POSITIONS.get(key) != null)
        window._logiq_guardian_scroll_position = SCROLL_POSITIONS.get(key)
      else window._logiq_guardian_scroll_position = undefined
    }

    router.events.on('routeChangeComplete', onRouteChangeComplete)

    window._logiq_guardian_scroll_position_set = function (
      key: string,
      scroll: number
    ) {
      SCROLL_POSITIONS.set(key, scroll)
    }

    return () => {
      router.events.off('routeChangeComplete', onRouteChangeComplete)
      delete window._logiq_guardian_scroll_position_set
    }
  }, [router])

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
        />
      </Head>

      <Sentry.ErrorBoundary fallback={GenericError}>
        <Suspense
          fallback={
            <div className="pointer-events-none fixed inset-0 grid place-items-center">
              <Loading />
            </div>
          }
        >
          <Provider value={client}>
            <PartnerTokenErrorBoundary>
              {props.children}
            </PartnerTokenErrorBoundary>
          </Provider>
        </Suspense>
      </Sentry.ErrorBoundary>
    </>
  )
}

export function getGuardianLayout(page: React.ReactNode) {
  return <GuardianLayout>{page}</GuardianLayout>
}

class PartnerTokenErrorBoundary extends React.Component<
  { children?: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: {}) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: unknown) {
    if (isPartnerTokenError(error)) {
      return { hasError: true }
    }
  }

  componentDidCatch(error: unknown, _: unknown) {
    if (!isPartnerTokenError(error)) throw error

    resetUrqlClient()
    Router.push('/web/partner-admin').then(() =>
      this.setState({ hasError: false })
    )
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="grid h-full place-items-center">
          <Loading />
        </div>
      )
    }

    return this.props.children
  }
}
